import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';

import styles from './AdjustmentApprovalOptions.style';
import AdjustmentApprovalDialog from '../AdjustmentApprovalDialog';
import {
  approveAdjustment,
  approveReversalAdjustment,
  rejectAdjustment,
  rejectReversalAdjustment,
} from '../../actions/adjustments';
import useAPI from '../../hooks/useAPI';

const AdjustmentApprovalOptions = ({ isVisible, adjustment }) => {
  const [showAdjustmentApprovalDialog, setShowAdjustmentApprovalDialog] = useState(false);
  const [showAdjustmentRejectionDialog, setShowAdjustmentRejectionDialog] = useState(false);
  const isReversalRequest = useMemo(() => {
    if (!adjustment) {
      return false;
    }

    return adjustment?.lineItems.some(
      lineItem => !!lineItem.reversal && lineItem.status.toLowerCase() === 'approvalrequested',
    );
  }, [adjustment]);

  const onApproveAdjustment = () => {
    setShowAdjustmentApprovalDialog(true);
  };

  const onRejectAdjustment = () => {
    setShowAdjustmentRejectionDialog(true);
  };

  const onDismissApprovalDialog = () => {
    setShowAdjustmentApprovalDialog(false);
  };

  const onDismissRejectionDialog = () => {
    setShowAdjustmentRejectionDialog(false);
  };

  const onSuccessCallback = () => {
    setShowAdjustmentApprovalDialog(false);
    setShowAdjustmentRejectionDialog(false);
    window.location.reload();
  };

  const approveApi = useAPI(
    () => {
      if (isReversalRequest) {
        return approveReversalAdjustment(adjustment?._id);
      }

      return approveAdjustment({ adjustmentId: adjustment?._id });
    },
    [adjustment, isReversalRequest],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback,
    },
  );

  const rejectApi = useAPI(
    values => {
      const { rejectReason } = values;

      if (isReversalRequest) {
        return rejectReversalAdjustment(adjustment?._id, { rejectReason });
      }

      return rejectAdjustment({ adjustmentId: adjustment?._id, values });
    },
    [adjustment, isReversalRequest],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback,
    },
  );

  const onApproveSubmit = async () => {
    await approveApi.fetchApi();
  };

  const onRejectSubmit = async values => {
    await rejectApi.fetchApi(values);
  };

  return (
    <>
      {showAdjustmentApprovalDialog && (
        <AdjustmentApprovalDialog
          isApproved
          error={approveApi.error}
          isSubmitting={approveApi.isLoading}
          onSubmit={onApproveSubmit}
          isOpen={showAdjustmentApprovalDialog}
          onDismiss={onDismissApprovalDialog}
        />
      )}
      {showAdjustmentRejectionDialog && (
        <AdjustmentApprovalDialog
          isApproved={false}
          onSubmit={onRejectSubmit}
          isReasonRequired
          isSubmitting={rejectApi.isLoading}
          error={rejectApi.error}
          isOpen={showAdjustmentRejectionDialog}
          onDismiss={onDismissRejectionDialog}
        />
      )}
      {isVisible && (
        <div>
          <PrimaryButton
            type="submit"
            onClick={onApproveAdjustment}
            className={styles.actionButton}
            data-testid="approval-button"
          >
            Approve
          </PrimaryButton>
          <DefaultButton
            onClick={onRejectAdjustment}
            className={styles.actionButton}
            data-testid="rejection-button"
          >
            Reject
          </DefaultButton>
        </div>
      )}
    </>
  );
};

AdjustmentApprovalOptions.propTypes = {
  isVisible: PropTypes.bool,
  adjustment: PropTypes.object,
};

AdjustmentApprovalOptions.defaultProps = {
  isVisible: true,
  adjustment: null,
};

export default AdjustmentApprovalOptions;
